/**
 * Date helpers for Waktu Indonesia Barat (UTC+7, no daylight saving).
 * Every function is deterministic for a given input, so the same output
 * is produced on the server and in the browser.
 */

const WIB_OFFSET_MS = 7 * 60 * 60 * 1000

export const WEEKDAY_SHORT = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab']
export const WEEKDAY_LONG = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
export const MONTH_LONG = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember']

const pad = (value: number): string => String(value).padStart(2, '0')

/** Shifts a moment so its UTC fields read as the WIB wall clock */
export const toWibClock = (now: Date = new Date()): Date => {
    return new Date(now.getTime() + WIB_OFFSET_MS)
}

export const toIsoDate = (date: Date): string => {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

export const parseIsoDate = (iso: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)

    if (!match) return null

    const [, year, month, day] = match
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

    return toIsoDate(date) === iso ? date : null
}

/** Next working days (Senin to Sabtu) in WIB, starting tomorrow */
export const getUpcomingWorkingDays = (count: number, now: Date = new Date()): string[] => {
    const wib = toWibClock(now)
    const cursor = new Date(Date.UTC(wib.getUTCFullYear(), wib.getUTCMonth(), wib.getUTCDate()))
    const days: string[] = []

    while (days.length < count) {
        cursor.setUTCDate(cursor.getUTCDate() + 1)

        if (cursor.getUTCDay() !== 0) days.push(toIsoDate(cursor))
    }

    return days
}

/** 0 = Minggu ... 6 = Sabtu */
export const weekdayOf = (iso: string): number => {
    return parseIsoDate(iso)?.getUTCDay() ?? 0
}

/** "Sen 2 Nov" */
export const formatDayShort = (iso: string): string => {
    const date = parseIsoDate(iso)

    if (!date) return iso

    return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]}`
}

/** "Sen, 2 Nov 2026" */
export const formatDayMedium = (iso: string): string => {
    const date = parseIsoDate(iso)

    if (!date) return iso

    return `${WEEKDAY_SHORT[date.getUTCDay()]}, ${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

/** "Senin, 2 November 2026" */
export const formatDayLong = (iso: string): string => {
    const date = parseIsoDate(iso)

    if (!date) return iso

    return `${WEEKDAY_LONG[date.getUTCDay()]}, ${date.getUTCDate()} ${MONTH_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

/** "2–5 November 2026", or "30 Nov – 3 Des 2026" across months */
export const formatDateRange = (first: string, last: string): string => {
    const start = parseIsoDate(first)
    const end = parseIsoDate(last)

    if (!start || !end) return `${first} – ${last}`

    if (start.getUTCFullYear() !== end.getUTCFullYear()) {
        return `${start.getUTCDate()} ${MONTH_SHORT[start.getUTCMonth()]} ${start.getUTCFullYear()} – ${end.getUTCDate()} ${MONTH_SHORT[end.getUTCMonth()]} ${end.getUTCFullYear()}`
    }

    if (start.getUTCMonth() !== end.getUTCMonth()) {
        return `${start.getUTCDate()} ${MONTH_SHORT[start.getUTCMonth()]} – ${end.getUTCDate()} ${MONTH_SHORT[end.getUTCMonth()]} ${end.getUTCFullYear()}`
    }

    return `${start.getUTCDate()}–${end.getUTCDate()} ${MONTH_LONG[end.getUTCMonth()]} ${end.getUTCFullYear()}`
}
